import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency, CurrencySelector } from '../../contexts/CurrencyContext';
import BottomNav from '../../components/BottomNav';
import { ThemeSelector } from '../../components/ThemeToggle';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  User, SignOut, Storefront, Question, Gift, QrCode, 
  Wallet, Trophy, CaretRight, Info, Star, Lightning,
  ArrowsClockwise, CreditCard, Moon, CurrencyGbp
} from '@phosphor-icons/react';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const { currency, currencyInfo } = useCurrency();
  const [showGuide, setShowGuide] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const quickGuide = [
    {
      icon: QrCode,
      title: "Scanner",
      desc: "Scannez le QR code du marchand à chaque visite pour gagner des points automatiquement."
    },
    {
      icon: Star,
      title: "Cumuler",
      desc: "Vos points s'accumulent à chaque scan. 100 points = £1 de valeur."
    },
    {
      icon: ArrowsClockwise,
      title: "Convertir",
      desc: "Convertissez vos points en crédit depuis votre portefeuille."
    },
    {
      icon: Gift,
      title: "Offrir",
      desc: "Achetez des cartes cadeaux pour vos proches, valables partout dans le réseau."
    }
  ];

  const menuItems = [
    {
      icon: Question,
      label: "Comment ça marche",
      desc: "Comprendre le fonctionnement",
      action: () => navigate('/how-it-works'),
      highlight: true
    },
    {
      icon: Storefront,
      label: "Devenir marchand",
      desc: "Rejoindre le réseau",
      action: () => navigate('/merchant/onboard'),
      show: user?.role === 'consumer'
    },
    {
      icon: CreditCard,
      label: "Mon portefeuille",
      desc: "Points et cartes cadeaux",
      action: () => navigate('/wallet')
    },
    {
      icon: Trophy,
      label: "Mes récompenses",
      desc: "Voir mes avantages",
      action: () => navigate('/rewards')
    }
  ];

  return (
    <div className="min-h-screen bg-[#18261E] pb-32">
      {/* Header */}
      <div className="px-5 pt-8 pb-6">
        <p className="text-[#C3A365] text-xs tracking-widest uppercase mb-1">Mon Compte</p>
        <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Profil
        </h1>
      </div>

      {/* User Card */}
      <div className="px-5 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1E2F26] rounded-2xl p-5 border border-[#283D32]"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#C3A365] to-[#A08245] rounded-full flex items-center justify-center text-[#18261E] text-2xl font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              <h2 className="text-xl text-white font-medium" data-testid="user-name">
                {user?.name || 'Utilisateur'}
              </h2>
              <p className="text-[#8BA89A] text-sm" data-testid="user-email">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-[#283D32] rounded-full text-[#C3A365] text-xs uppercase tracking-wider">
                {user?.role === 'consumer' ? 'Membre' : user?.role}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Help Banner */}
      <div className="px-5 mb-6">
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => setShowGuide(!showGuide)}
          className="w-full bg-gradient-to-r from-[#C3A365]/20 to-[#C3A365]/5 border border-[#C3A365]/30 rounded-xl p-4 flex items-center justify-between"
          data-testid="quick-guide-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C3A365]/20 flex items-center justify-center">
              <Lightning size={20} className="text-[#C3A365]" weight="fill" />
            </div>
            <div className="text-left">
              <p className="text-white font-medium text-sm">Guide rapide</p>
              <p className="text-[#8BA89A] text-xs">Nouveau ? Comprenez tout en 30s</p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: showGuide ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <CaretRight size={18} className="text-[#C3A365]" />
          </motion.div>
        </motion.button>

        {/* Expandable Quick Guide */}
        <AnimatePresence>
          {showGuide && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-3 space-y-2">
                {quickGuide.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-[#1E2F26] rounded-xl p-4 flex items-start gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#283D32] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon size={16} className="text-[#C3A365]" weight="duotone" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium mb-0.5">{item.title}</p>
                        <p className="text-[#8BA89A] text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="w-full py-3 text-[#C3A365] text-sm font-medium flex items-center justify-center gap-2"
                >
                  Voir le guide complet <CaretRight size={14} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Appearance Settings */}
      <div className="px-5 mb-6">
        <p className="text-[#8BA89A] text-xs uppercase tracking-wider mb-3">
          {language === 'fr' ? 'Apparence' : 'Appearance'}
        </p>
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#283D32] flex items-center justify-center">
              <Moon size={20} className="text-[#C3A365]" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">
                {language === 'fr' ? 'Thème' : 'Theme'}
              </p>
              <p className="text-[#8BA89A] text-xs">
                {language === 'fr' ? 'Choisissez votre mode d\'affichage' : 'Choose your display mode'}
              </p>
            </div>
          </div>
          <ThemeSelector language={language} />
        </div>
      </div>

      {/* Currency Settings */}
      <div className="px-5 mb-6">
        <p className="text-[#8BA89A] text-xs uppercase tracking-wider mb-3">
          {language === 'fr' ? 'Devise' : 'Currency'}
        </p>
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#283D32] flex items-center justify-center">
              <CurrencyGbp size={20} className="text-[#C3A365]" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">
                {language === 'fr' ? 'Devise d\'affichage' : 'Display Currency'}
              </p>
              <p className="text-[#8BA89A] text-xs">
                {language === 'fr' 
                  ? `Actuellement: ${currencyInfo?.name} (${currencyInfo?.symbol})`
                  : `Currently: ${currencyInfo?.name} (${currencyInfo?.symbol})`
                }
              </p>
            </div>
          </div>
          <CurrencySelector />
        </div>
      </div>

      {/* Menu Items */}
      <div className="px-5 mb-6">
        <p className="text-[#8BA89A] text-xs uppercase tracking-wider mb-3">Menu</p>
        <div className="space-y-2">
          {menuItems.filter(item => item.show !== false).map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.05 }}
                onClick={item.action}
                className={`w-full rounded-xl p-4 flex items-center justify-between transition-all ${
                  item.highlight 
                    ? 'bg-[#145A3A] border border-[#1A734A]' 
                    : 'bg-[#1E2F26] border border-[#283D32]'
                }`}
                data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    item.highlight ? 'bg-[#1A734A]' : 'bg-[#283D32]'
                  }`}>
                    <Icon size={20} className={item.highlight ? 'text-[#C3A365]' : 'text-[#8BA89A]'} weight="duotone" />
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-medium">{item.label}</p>
                    <p className="text-[#8BA89A] text-xs">{item.desc}</p>
                  </div>
                </div>
                <CaretRight size={18} className={item.highlight ? 'text-[#C3A365]' : 'text-[#8BA89A]'} />
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Logout */}
      <div className="px-5">
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          onClick={handleLogout}
          className="w-full bg-[#1E2F26] border border-[#3E5248] text-white py-4 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-[#283D32] transition-colors"
          data-testid="logout-button"
        >
          <SignOut size={18} className="text-[#C3A365]" />
          Se déconnecter
        </motion.button>

        <div className="mt-8 text-center">
          <p className="brand-logo text-lg text-[#8BA89A]/50">habitué</p>
          <p className="text-[#8BA89A]/30 text-xs mt-1">v1.0 · © 2025</p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;
